import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useCreditLedger } from "@/api/use-payment-options-api";
import { Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CreditHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function CreditHistoryDialog({
  open,
  onOpenChange,
}: CreditHistoryDialogProps) {
  const { t, i18n } = useTranslation("app");
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCreditLedger();

  // balanceCents is the live total repeated on every page; entries accumulate.
  const balanceCents = data?.pages[0]?.balanceCents ?? 0;
  const entries = data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            <span>{t("creditHistory.title")}</span>
          </div>
          <span className="text-small font-normal text-default-500">
            {t("creditHistory.currentBalance", {
              amount: formatCurrency(balanceCents),
            })}
          </span>
        </ModalHeader>
        <ModalBody className="pb-6">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          )}

          {error && (
            <p className="text-small text-danger py-4">
              {t("creditHistory.loadError")}
            </p>
          )}

          {data && entries.length === 0 && (
            <p className="text-small text-default-500 py-8 text-center">
              {t("creditHistory.empty")}
            </p>
          )}

          {entries.length > 0 && (
            <div className="space-y-1">
              {entries.map((entry) => {
                const moneyIn = entry.amountCents > 0;
                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-default-50"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {t(`creditHistory.types.${entry.type}`, {
                            defaultValue: entry.type,
                          })}
                        </span>
                        {entry.type === "DEPOSIT_REVERSAL" && (
                          <Chip size="sm" variant="flat" color="danger">
                            {t("creditHistory.chargeback")}
                          </Chip>
                        )}
                      </div>
                      <p className="text-tiny text-default-500 truncate">
                        {new Date(entry.createdAt).toLocaleDateString(
                          i18n.language === "zh" ? "zh-CN" : "en-CA",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                        {entry.orderId
                          ? ` · ${t("creditHistory.orderSuffix", {
                              orderId: entry.orderId.slice(-6),
                            })}`
                          : ""}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p
                        className={`text-sm font-semibold ${
                          moneyIn ? "text-success-600" : "text-default-600"
                        }`}
                      >
                        {moneyIn ? "+" : ""}
                        {formatCurrency(entry.amountCents)}
                      </p>
                      <p className="text-tiny text-default-400">
                        {formatCurrency(entry.balanceAfterCents)}
                      </p>
                    </div>
                  </div>
                );
              })}

              {hasNextPage && (
                <div className="flex justify-center pt-2">
                  <Button
                    size="sm"
                    variant="flat"
                    onPress={() => void fetchNextPage()}
                    isLoading={isFetchingNextPage}
                  >
                    {t("actions.loadOlder")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
